/**
 * @author jakubmaslon
 * @since 2020-02-12
 */

import * as React from "react";
import styled, { css } from "styled-components";

import { BOTTOM_NAVIGATION_SPACE_KEEPER_HEIGHT } from "@config/consts";

import { Section, Heading, Margins, TestWrapper } from "@ui/Atoms";
import { Divider } from "@ui/Atoms/Divider";
import { rem } from "@ui/helpers";

type Variant = "default" | "smallPadding";

interface Props {
  header?: string;
  children?: React.ReactNode;
  isMoved?: boolean;
  testId?: string;
  className?: string;
  variant?: Variant;
}

const Box = (props: Props) => (
  <TestWrapper testId={props.testId}>
    <BoxWrapper
      className={props.className}
      variant={props.variant}
      isMoved={props.isMoved}
      small
    >
      {!!props.header && (
        <Margins xs={[null, "base_x4", null, null]}>
          <Heading type="h6">{props.header}</Heading>
        </Margins>
      )}
      {props.children}
    </BoxWrapper>
  </TestWrapper>
);

export { Box, BoxDivider };

const BoxWrapper = styled(Section)<{ variant?: Variant; isMoved?: boolean }>`
  --box-horizontal-padding: ${props => props.theme.margins.base_x2};
  --box-vertical-padding: ${props => props.theme.margins.base_x3};

  position: fixed;
  right: 0;
  bottom: ${props =>
    props.isMoved ? `${rem(BOTTOM_NAVIGATION_SPACE_KEEPER_HEIGHT)}` : 0};
  left: 0;
  z-index: 10;
  display: flex;
  background: ${props => props.theme.palette.white};
  margin: 0;
  padding: var(--box-vertical-padding) var(--box-horizontal-padding);
  font-size: ${props => props.theme.fonts.sizes.caption};
  color: ${props => props.theme.palette.grey800};
  border-top-left-radius: ${props => props.theme.border.radius.base_x1_5};
  border-top-right-radius: ${props => props.theme.border.radius.base_x1_5};
  box-shadow: 0 0 ${rem(4)} 0 rgb(0 0 0 / 20%), 0 0 ${rem(6)} 0 rgb(0 0 0 / 7%);

  transition: bottom ${props => props.theme.animations.duration}
    ${props => props.theme.animations.durationShort}
    ${props => props.theme.animations.easing};

  ${props => props.theme.breakpoints.tablet} {
    --box-horizontal-padding: ${props => props.theme.margins.base_x5};
    --box-vertical-padding: ${props => props.theme.margins.base_x5};

    margin: 0;

    ${props =>
      props.variant === "smallPadding" &&
      css`
        --box-horizontal-padding: ${props => props.theme.margins.base_x3};
      `}
  }

  ${props => props.theme.breakpoints.desktop} {
    position: static;
    display: block;
    margin: ${props => props.theme.margins.base} 0;
    box-shadow: none;
    font-size: ${props => props.theme.fonts.sizes.body2};
    color: ${props => props.theme.palette.grey700};
    border-radius: ${({ theme }) => theme.buttons.borderRadius};
  }
`;

const BoxDivider = styled(Divider)`
  width: calc(100% + var(--box-horizontal-padding) * 2);
  margin: ${props => props.theme.margins.base_x3}
    calc(var(--box-horizontal-padding) * -1);
`;
